import { Button } from '@sisumail/ui';
import {
    FileText,
    LogOut,
    RefreshCw,
    Shield,
    Users,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useActivateIdentity,
    useIdentities,
    useLogs,
    useRevokeIdentity,
    useStats,
} from '../hooks/useAdminData';

import { Badge } from '../components/dashboard/Badge';
import { IdentitiesTable } from '../components/dashboard/IdentitiesTable';
import { LogsTable } from '../components/dashboard/LogsTable';
import { StatsCards } from '../components/dashboard/StatsCards';

export default function Dashboard() {
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem('admin_secret')) {
            navigate('/login');
        }
    }, [navigate]);

    const [tab, setTab] = useState<'users' | 'logs'>('users');

    // Filter State
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const LIMIT = 50;

    // Log Filter State
    const [logSource, setLogSource] = useState<string>('');
    const [logLevel, setLogLevel] = useState<string>('');
    const [logRequestId, setLogRequestId] = useState<string>('');
    const [logUserId, setLogUserId] = useState<string>('');
    const [logPage, setLogPage] = useState(0);
    const LOG_LIMIT = 50;

    // Queries
    const { data: stats } = useStats();

    // Identities Query
    const { data: identitiesData, isLoading: identitiesLoading } = useIdentities(
        page,
        LIMIT,
        debouncedSearch
    );
    const identities = identitiesData?.identities || [];
    const totalIdentities = identitiesData?.total || 0;

    // Logs Query
    const { data: logsData, isLoading: logsLoading } = useLogs(
        logPage,
        LOG_LIMIT,
        logLevel,
        logSource,
        logRequestId,
        logUserId
    );
    const logs = logsData?.logs || [];
    const totalLogs = logsData?.total || 0;

    const loading = tab === 'users' ? identitiesLoading : logsLoading;

    // Mutations
    const { mutateAsync: activateIdentity } = useActivateIdentity();
    const { mutateAsync: revokeIdentity } = useRevokeIdentity();

    // Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const handleLogout = () => {
        localStorage.removeItem('admin_secret');
        navigate('/login');
    };

    const handleActivate = async (username: string) => {
        if (!confirm(`Force activate subscription for "${username}"?`)) return;

        try {
            await activateIdentity(username);
            alert(`Identity "${username}" activated.`);
        } catch (_e) {
            alert('Failed to activate');
        }
    };

    const handleRevoke = async (username: string) => {
        if (!confirm(`Revoke identity "${username}"? IRREVERSIBLE.`)) return;
        try {
            await revokeIdentity(username);
            alert(`Identity "${username}" revoked.`);
        } catch (_e) {
            alert('Failed to revoke');
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 p-4 font-sans text-gray-100 sm:p-8">
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <header className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                    <h1 className="flex items-center gap-3 font-bold text-3xl">
                        <Shield className="text-emerald-500" /> Sisumail Admin{' '}
                        <Badge state="v3.0.0" />
                    </h1>
                    <div className="flex gap-4">
                        <Button
                            variant="secondary"
                            onClick={() => window.location.reload()}
                            className="flex items-center gap-2"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />{' '}
                            Refresh
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleLogout}
                            className="flex items-center gap-2"
                        >
                            <LogOut size={16} /> Logout
                        </Button>
                    </div>
                </header>

                {/* KPI Cards */}
                <StatsCards stats={stats} />

                {/* Tabs */}
                <div className="flex border-gray-700 border-b">
                    <button
                        type="button"
                        onClick={() => setTab('users')}
                        className={`flex items-center gap-2 px-6 py-3 font-medium text-sm ${tab === 'users' ? 'border-amber-500 border-b-2 text-amber-500' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Users size={16} /> Users
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('logs')}
                        className={`flex items-center gap-2 px-6 py-3 font-medium text-sm ${tab === 'logs' ? 'border-amber-500 border-b-2 text-amber-500' : 'text-gray-400 hover:text-white'}`}
                    >
                        <FileText size={16} /> Logs
                    </button>
                </div>

                {/* Tab Content */}
                {tab === 'users' ? (
                    <IdentitiesTable
                        identities={identities}
                        total={totalIdentities}
                        page={page}
                        limit={LIMIT}
                        search={search}
                        onSearchChange={(val) => {
                            setSearch(val);
                            setPage(0);
                        }}
                        onPageChange={setPage}
                        onActivate={handleActivate}
                        onRevoke={handleRevoke}
                    />
                ) : (
                    <LogsTable
                        logs={logs}
                        total={totalLogs}
                        page={logPage}
                        limit={LOG_LIMIT}
                        onPageChange={setLogPage}
                        source={logSource}
                        onSourceChange={(v) => { setLogSource(v); setLogPage(0); }}
                        level={logLevel}
                        onLevelChange={(v) => { setLogLevel(v); setLogPage(0); }}
                        traceId={logRequestId}
                        onTraceIdChange={(v) => { setLogRequestId(v); setLogPage(0); }}
                        userId={logUserId}
                        onUserIdChange={(v) => { setLogUserId(v); setLogPage(0); }}
                    />
                )}
            </div>
        </div>
    );
}
